import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import Editor, { DEFAULT_TEXT } from "../../../../../components/editor/Editor";
import ChannelTags from "../../../../../components/pickers/channel-tags/ChannelTags";
import SlackBots from "../../../../../components/pickers/slack-bots/SlackBots";
import Select from "../../../../../components/select/Select";
import TagList from "../../../../../components/tag-list/TagList";
import useRouting, { Link } from "../../../../../contexts/Routing";
import {
    ChannelMode, ChatSettingsSlack, ChatSettingsSlackInput,
} from "../../../../../__generated__/api";
import ChannelModeHelp from "../../channels/slack/ChannelModeHelp";
import EmojiActions from "../../channels/slack/EmojiActions";
import TicketCreateHelp from "../../channels/slack/TicketCreateHelp";
import ExpandingSection from "../../../../../components/expanding-section/ExpandingSection";
import BooleanSelect from "../../../../../components/pickers/boolean/BooleanSelect";

const CHANNEL_MODES = {
    [ChannelMode.Threaded]: "Threaded",
    [ChannelMode.SingleThread]: "One ticket at a time",
    [ChannelMode.Manual]: "Manual",
};

export interface Props {
    data: ChatSettingsSlack | undefined,
    onChange: (options: ChatSettingsSlackInput) => void;
    shouldAllowAutomation?: boolean;
    canAddTags: boolean;
}

export default function SlackSettings({
    data, onChange, shouldAllowAutomation, canAddTags,
}: Props): ReactElement {
    const { Paths } = useRouting();

    const [tags, setTags] = useState(() => data?.tags ?? []);
    const [channelMode, setChannelMode] = useState(() => data?.channelMode);
    const [approvedBotIds, setApprovedBotIds] = useState(() => data?.approvedBotIds ?? []);

    const [ignoreTicketsFromEmailDomains, setIgnoreTicketsFromEmailDomains] = useState(() => data?.ignoreTicketsFromEmailDomains);
    const [ticketCreatedTemplate, setTicketCreatedTemplate] = useState(() => data?.ticketCreatedTemplate || DEFAULT_TEXT);
    const [emojiActions, setEmojiActions] = useState(() => data?.emojiActions ?? []);
    const [canFillOutForm, setCanFillOutForm] = useState(() => data?.canFillOutForm ?? true);

    useEffect(() => {
        setTags(data?.tags ?? []);
        setChannelMode(data?.channelMode);
        setIgnoreTicketsFromEmailDomains(data?.ignoreTicketsFromEmailDomains);
        setApprovedBotIds(data?.approvedBotIds ?? []);
        setEmojiActions(data?.emojiActions ?? []);
        setCanFillOutForm(data?.canFillOutForm ?? true);
        setTicketCreatedTemplate(data?.ticketCreatedTemplate || DEFAULT_TEXT);
    }, [data]);

    useEffect(() => {
        if ((!tags.equals(data?.tags ?? []))
            || (data?.channelMode !== channelMode)
            || (!(ignoreTicketsFromEmailDomains ?? []).equals(data?.ignoreTicketsFromEmailDomains ?? []))
            || (!approvedBotIds.equals(data?.approvedBotIds ?? []))
            || (data?.emojiActions && !emojiActions.equals(data?.emojiActions))
            || (canFillOutForm != null && canFillOutForm !== data?.canFillOutForm)
            || (ticketCreatedTemplate !== (data?.ticketCreatedTemplate || DEFAULT_TEXT))
        ) {
            onChange({
                tags,
                channelMode,
                ignoreTicketsFromEmailDomains,
                approvedBotIds,
                ticketCreatedTemplate,
                emojiActions,
                canFillOutForm,
                name: data?.name,
            });
        }
    }, [tags, channelMode, ignoreTicketsFromEmailDomains, approvedBotIds, ticketCreatedTemplate, emojiActions, canFillOutForm]);

    const advancedSettings = (
        <>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bolder">
                    Channel Mode
                    {" "}
                    <ChannelModeHelp />
                </Form.Label>

                <Select
                    className="text-gray"
                    placeholder=""
                    isClearable
                    onChange={value => setChannelMode(value?.value as ChannelMode)}
                    options={Object.entries(CHANNEL_MODES).map(([value, label]) => ({ value, label }))}
                    value={!channelMode ? null : {
                        value: channelMode,
                        label: CHANNEL_MODES[channelMode],
                    }}
                />
                <Form.Text>
                    How do you when tickets to be created in this Slack channel?
                    <ChannelModeHelp buttonClassName="m-0 ms-1" />
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bolder">Ignore Internal Messages</Form.Label>
                <TagList
                    placeholder="@example.com"
                    value={ignoreTicketsFromEmailDomains as string[] ?? []}
                    onChange={value => setIgnoreTicketsFromEmailDomains(value)}
                    canNotHaveDuplicates
                />
                <Form.Text>
                    Lets say your marketing team is always sending promotional content in
                    customer channels. You don&apos;t want a ticket created for each one. By adding
                    their email address or entire email domain (such as @example.com) into this
                    box, we will not create a ticket for any message that match a users email or email domain.
                    This setting overrides the same setting in the general configuration.
                    If any domains or emails are present in this list, we will ignore the general setting.
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
                <Form.Label className="fw-bolder">
                    Approved Bots
                </Form.Label>
                <SlackBots
                    isMulti
                    placeholder="Which bot messages should not be ignored."
                    value={approvedBotIds as string[]}
                    onSelected={selected => setApprovedBotIds(selected)}
                />
                <Form.Text>
                    By default, Agent always blocks messages from other bots in Slack. However, you can
                    override this behavior and have Agent injest messages and create tickets from other
                    bots in your Slack workspace
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
                <Form.Label className="fw-bolder">
                    Ticket Created Message Template
                    {" "}
                    <TicketCreateHelp />
                </Form.Label>
                <div className="bg-white border-light border rounded">
                    <Editor
                        text={ticketCreatedTemplate as string || DEFAULT_TEXT}
                        onChange={msg => {
                            setTicketCreatedTemplate(msg);
                        }}
                        isScrollDisabled
                        placeholder="New request created: {{SUBJECT}}"
                        shouldFocus={false}
                    />
                </div>
                <Form.Text>
                    When a ticket is created and a new thread is posted in the channel,
                    what information should this ticket contain?
                    <TicketCreateHelp buttonClassName="m-0 ms-1" />
                </Form.Text>
            </Form.Group>
            <Form.Group className="mb-4">
                <Form.Label className="fw-bolder">
                    Fill out form prompt
                </Form.Label>
                <div className="bg-white border-light border rounded">
                    <BooleanSelect
                        value={canFillOutForm as boolean}
                        onChange={value => setCanFillOutForm(value ?? true)}
                    />
                </div>
                <Form.Text>
                    When creating a ticket, if a request type is specified in a channel, the will see a
                    &quot;Fill out form&quot;
                    button to provide more context and fill out the request type. However, in the case of
                    a channel menu, you may not want that option. By setting this to No, users will not see
                    the option to fill out the request.
                </Form.Text>
            </Form.Group>
        </>
    );

    return (
        <>
            <div className="border p-3 rounded bg-white mt-4 mb-4">
                <h5 className="mb-3 text-light">
                    Slack
                    {" "}
                    {data?.name ? `(#${data.name})` : ""}
                </h5>

                {canAddTags && (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">
                            Channel Tags
                        </Form.Label>
                        <ChannelTags
                            placeholder="Please pick a list of channel tags"
                            isMulti
                            onSelected={selected => setTags(selected)}
                            value={tags}
                            canCreate
                        />
                        <Form.Text>
                            Which tags should be associated with this queue. To manage channel tags, visit
                            {" "}
                            <Link
                                to={Paths.ADMINISTRATOR_SETTING}
                                params={{ setting: "channel-tags" }}
                                className="link-info text-decoration-none"
                            >
                                General Settings
                            </Link>
                            .
                        </Form.Text>
                    </Form.Group>
                )}

                {!canAddTags ? advancedSettings : (
                    <ExpandingSection title="Channel Overrides" classBackground="bg-gray-100">
                        {advancedSettings}
                    </ExpandingSection>
                )}

            </div>
            {shouldAllowAutomation && (
                <EmojiActions actions={emojiActions} onChange={setEmojiActions} />
            )}

        </>
    );
}

SlackSettings.defaultProps = {
    shouldAllowAutomation: false,
};
